/**
 * Delivery selected from the listing page's instant quote panel.
 *
 * When a buyer gets an instant delivery quote, the ZIP and delivery type are remembered
 * for that listing so the order panel and checkout can add delivery to the same card payment.
 * Only the ZIP and residential flag are stored: the server recalculates the freight price
 * itself (see server/api-util/serverFreight.js), so a buyer can't change the amount charged.
 */
const STORAGE_KEY = 'ctFreightSelection';
export const FREIGHT_SELECTION_EVENT = 'ct:freight-selection';

const isBrowser = () => typeof window !== 'undefined';

const readAll = () => {
  if (!isBrowser()) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
};

const writeAll = all => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    // storage may be blocked; the in-page event still updates the order panel
  }
};

const notify = (listingId, selection) => {
  try {
    window.dispatchEvent(
      new CustomEvent(FREIGHT_SELECTION_EVENT, { detail: { listingId, selection } })
    );
  } catch (e) {
    // ignore
  }
};

/** @returns {{ deliveryZip: string, isResidential: boolean } | null} */
export const getFreightSelection = listingId => {
  if (!listingId) return null;
  const s = readAll()[listingId];
  return s && /^\d{5}$/.test(s.deliveryZip)
    ? { deliveryZip: s.deliveryZip, isResidential: !!s.isResidential }
    : null;
};

export const setFreightSelection = (listingId, { deliveryZip, isResidential }) => {
  if (!isBrowser() || !listingId) return;
  const selection = { deliveryZip, isResidential: !!isResidential };
  writeAll({ ...readAll(), [listingId]: selection });
  notify(listingId, selection);
};

export const clearFreightSelection = listingId => {
  if (!isBrowser() || !listingId) return;
  const { [listingId]: _removed, ...rest } = readAll();
  writeAll(rest);
  notify(listingId, null);
};

/** orderData fragment sent to this app's server: { ctFreight: {...} } or {} */
export const freightOrderDataMaybe = listingId => {
  const selection = getFreightSelection(listingId);
  return selection ? { ctFreight: selection } : {};
};
